import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, BellRing } from 'lucide-react';
import { Venue, UserProfile, toggleAlert } from '../services/api';
import { Analytics } from '../services/analytics';

interface VenueAlertButtonProps {
  venue: Venue;
  userProfile: UserProfile | null;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile | null>>;
}

const BUTTON_BASE = `w-full min-h-[44px] mt-1.5 px-4 py-2.5 text-sm font-medium rounded-full
  transition-all duration-300 flex items-center justify-center gap-1 select-none active:scale-95`;

const VenueAlertButton: React.FC<VenueAlertButtonProps> = ({ venue, userProfile, setUserProfile }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isOn = (userProfile?.alert_venue_ids ?? []).includes(venue.id);
  const atLimit = (userProfile?.alert_venue_ids ?? []).length >= 5;

  // Existing selections can always be removed, including retired venues.
  if (!isOn && (!venue.tracks_availability || venue.venue_type !== 'RESTAURANT')) {
    return null;
  }

  if (!userProfile?.is_pro && !isOn) {
    return (
      <Link
        to="/alerts"
        className={`${BUTTON_BASE} bg-ink-50 text-ink-600 hover:bg-ink-100`}
        title="Get an email when new tables open here"
      >
        <Bell className="w-3 h-3" />
        Alert me
        <span className="ml-0.5 rounded-full bg-clay px-1.5 py-0.5 text-[10px] leading-none text-white">
          PRO
        </span>
      </Link>
    );
  }


  const handleClick = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await toggleAlert(venue.id, !isOn);
      setUserProfile(prev => (prev ? { ...prev, alert_venue_ids: updated.alert_venue_ids } : prev));
      Analytics.logEvent('Venue Alert', isOn ? 'Off' : 'On', { venue: venue.name });
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Couldn't update the alert. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={saving || (!isOn && (atLimit || !userProfile?.user.email))}
        aria-pressed={isOn}
        aria-label={`${isOn ? 'Turn off' : 'Enable'} alerts for ${venue.name}`}
        aria-busy={saving}
        title={isOn ? "We'll email you when new tables open here" : 'Get an email when new tables open here'}
        className={`${BUTTON_BASE} disabled:opacity-60 ${
          isOn
            ? 'bg-brand-100 text-brand-800 hover:bg-brand-200'
            : 'bg-brand-600 text-white hover:bg-brand-700'
        }`}
      >
        {isOn ? <BellRing className="w-3 h-3" /> : <Bell className="w-3 h-3" />}
        {saving ? 'Saving…' : isOn ? 'Alerts on' : atLimit ? '5 alerts selected' : 'Alert me'}
      </button>
      {atLimit && !isOn && <Link to="/alerts" className="block mt-1 text-[11px] text-center text-brand-700 underline">Manage your 5 alerts</Link>}
      {error && <p role="alert" className="mt-1 text-[11px] text-red-600 text-center">{error}</p>}
    </>
  );
};

export default VenueAlertButton;
